import { extractPaletteColor } from 'base/styleguide/color';
import {
  generateScheme,
  getChromaticShadeName,
  getNeutralShadeName,
  getShadeKey,
} from 'base/styleguide/concept';
import { aDesignScheme, DesignScheme } from 'base/styleguide/model';
import {
  parseShadeKey,
  ShadeDescriptor,
  ShadeKey,
} from 'base/styleguide/types';
import { Button } from 'base/ui/button';
import { Icon } from 'base/ui/icon';
import { IconAdd, IconExternal, IconOption } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { MenuButton, useOnMouseupOutside } from 'base/ui/menu';
import { menuDivider, menuItem, MenuModel } from 'base/ui/menu_model';
import { PanelLayout } from 'base/ui/panel';
import { TextSmall, TextSmallLight } from 'base/ui/typography';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getGlobalDesignStore } from '../design/design_store';
import { EditorMode, editorModeStore } from '../editor_mode';
import { Project } from '../model';
import { unrefSection } from '../model_helpers';
import { SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import { SidebarNavigation } from '../sidebar/routes';
import { ConceptAPI } from '../styleguide/concept_store';
import { styleguideConceptId } from '../styleguide/routes';
import styles from './design_schemes.css';

export const DesignSchemesPanel = observer(function _DesignSchemesPanel({
  session,
  navigation,
  selection,
}: {
  session: ProjectSession;
  navigation: SidebarNavigation;
  selection: SelectionStore;
}) {
  const [schemeEditorState, setSchemeEditorState] = useState<{
    designScheme: DesignScheme;
    schemeNumber: number;
  } | null>(null);

  const concept = getGlobalDesignStore().design.linkedConcept;

  const sel = selection.get();
  const sref = sel.section;
  if (sref == null) {
    return null;
  }

  const section = unrefSection(sref, session.project);
  const elementId = section?.element.current?.id;
  if (elementId == null) {
    return null;
  }

  const selectedDesignScheme = concept.getDesignSchemeByElementId(elementId);

  return (
    <>
      <PanelLayout title="Schemes" navigation={navigation}>
        <Rows spacing={16}>
          <Rows spacing={8}>
            {concept.designSchemes.map((designScheme, idx) => (
              <SchemeRowWithActions
                key={idx}
                designScheme={designScheme}
                schemeNumber={idx + 1}
                concept={concept}
                selected={designScheme.id === selectedDesignScheme?.id}
                elementId={elementId}
                onEditScheme={() =>
                  setSchemeEditorState({
                    designScheme,
                    schemeNumber: idx + 1,
                  })
                }
                project={session.project}
              />
            ))}
            <Button
              variant="secondary"
              icon={IconAdd}
              onClick={() => {
                concept.addDesignScheme(
                  aDesignScheme({ shadeKey: 'neutral-shade-0' }),
                );
              }}
            />
          </Rows>
          <EditColorsRow concept={concept} />
        </Rows>
      </PanelLayout>
      {schemeEditorState != null &&
        createPortal(
          <SchemeEditorPanel
            onClose={() => setSchemeEditorState(null)}
            concept={concept}
            designScheme={schemeEditorState.designScheme}
            schemeNumber={schemeEditorState.schemeNumber}
            project={session.project}
          />,
          document.body,
        )}
    </>
  );
});

export const SchemeRow = observer(function _SchemeRow({
  designScheme,
  schemeNumber,
  concept,
  selected,
  onClick,
  ...props
}: {
  designScheme: DesignScheme;
  schemeNumber: number;
  concept: ConceptAPI;
  selected?: boolean;
  onClick: () => void;
} & (
  | { rightContent: JSX.Element; menu?: never }
  | { rightContent?: never; menu: MenuModel }
  | { rightContent?: never; menu?: never }
)) {
  const [menuOpen, setMenuOpen] = useState(false);

  const shade = parseShadeKey(designScheme.shadeKey);
  if (shade == null) {
    return null;
  }
  const compiledScheme = generateScheme(shade, concept);

  const primaryShadeKey = getShadeKey({
    ...shade,
    shade: shade.palette === 'neutral' ? 0 : 4,
  });

  return (
    <div
      onClick={onClick}
      className={classNames(styles.schemeRow, selected && styles.selected)}
    >
      <div className={styles.content}>
        <div className={styles.swatch}>
          <div
            className={styles.half}
            style={{
              backgroundColor: extractPaletteColor(concept, primaryShadeKey),
            }}
          />
          <div
            className={styles.half}
            style={{
              backgroundColor: extractPaletteColor(
                concept,
                compiledScheme.background,
              ),
            }}
          />
        </div>
        <TextSmall>Scheme {schemeNumber}</TextSmall>
      </div>
      {props.rightContent != null ? (
        <div className={classNames(styles.rightContent)}>
          {props.rightContent}
        </div>
      ) : props.menu != null ? (
        <div className={classNames(styles.rightContent)}>
          <div
            className={classNames(
              styles.moreButton,
              menuOpen && styles.menuOpen,
            )}
          >
            <MenuButton
              icon={IconOption}
              menu={props.menu}
              search="hidden"
              size="small"
              onOpen={() => setMenuOpen(true)}
              onClose={() => setMenuOpen(false)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
});

const EditColorsRow = observer(function _EditColorsRow({
  concept,
}: {
  concept: ConceptAPI;
}) {
  return (
    <div
      className={styles.schemeRow}
      onClick={() => {
        editorModeStore.set(EditorMode.Styleguide);
        styleguideConceptId.set(concept.state.uuid);
      }}
    >
      <div className={styles.content}>
        <div className={styles.colorsIcon}>
          <div className={styles.color} />
          <div className={styles.color} />
          <div className={styles.color} />
          <div className={styles.color} />
        </div>
        <TextSmall>Colors</TextSmall>
      </div>
      <div className={styles.rightContent}>
        <Icon icon={IconExternal} size={14} />
      </div>
    </div>
  );
});

const SchemeRowWithActions = observer(function _SchemeRow2({
  elementId,
  onEditScheme,
  project,
  ...passthrough
}: {
  elementId: string;
  onEditScheme: () => void;
  project: Project;
  designScheme: DesignScheme;
  schemeNumber: number;
  concept: ConceptAPI;
  selected: boolean;
}) {
  const { designScheme, concept } = passthrough;

  const menu = [
    menuItem({ label: 'Change template', onClick: onEditScheme }),
    menuDivider(),
    menuItem({
      label: 'Duplicate',
      onClick: () => {
        concept.addDesignScheme(
          aDesignScheme({ shadeKey: designScheme.shadeKey }),
        );
      },
    }),
    menuItem({
      label: 'Delete',
      onClick: () => {
        concept.deleteDesignScheme(designScheme);
      },
    }),
  ];

  const onClick = () => {
    concept.setDesignSchemeForElementId(elementId, designScheme);
  };

  return <SchemeRow {...passthrough} onClick={onClick} menu={menu} />;
});

const SchemeEditorPanel = observer(function _SchemeEditorPopup({
  onClose,
  concept,
  designScheme,
  schemeNumber,
  project,
}: {
  onClose: () => void;
  concept: ConceptAPI;
  designScheme: DesignScheme;
  schemeNumber: number;
  project: Project;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  useOnMouseupOutside(true, onClose, containerRef);

  const allShadeKeys = [
    ...concept.neutral.shades.map(
      (_, idx) =>
        ({ palette: 'neutral', shade: idx } satisfies ShadeDescriptor),
    ),
    ...concept.chromatics.flatMap((c, chromaticIdx) =>
      c.shades.map(
        (_, shadeIdx) =>
          ({
            palette: 'chromatic',
            chromatic: chromaticIdx + 1,
            shade: shadeIdx + 1,
          } satisfies ShadeDescriptor),
      ),
    ),
  ].map(getShadeKey);

  return (
    <div className={styles.subpanel} ref={containerRef}>
      <PanelLayout
        title={`Scheme ${schemeNumber}`}
        navigation={{ close: onClose }}
      >
        <Rows spacing={8}>
          {allShadeKeys.map((shadeKey, idx) => (
            <ShadeRow
              key={idx}
              onClick={() => {
                concept.setDesignSchemeShadeKey(designScheme, shadeKey);
              }}
              shadeKey={shadeKey}
              selected={designScheme.shadeKey === shadeKey}
              concept={concept}
            />
          ))}
        </Rows>
      </PanelLayout>
    </div>
  );
});

const ShadeRow = observer(function _ShadeRow({
  shadeKey,
  selected,
  onClick,
  concept,
}: {
  shadeKey: ShadeKey;
  selected: boolean;
  onClick: () => void;
  concept: ConceptAPI;
}) {
  const shade = parseShadeKey(shadeKey);
  if (shade == null) {
    return null;
  }

  const shadeName =
    shade.palette === 'neutral'
      ? getNeutralShadeName(shade.shade)
      : getChromaticShadeName(
          concept.chromatics[shade.chromatic - 1],
          shade.shade,
        );

  return (
    <div
      className={classNames(styles.shadeRow, selected && styles.selected)}
      onClick={onClick}
    >
      <div
        className={styles.shadeSwatch}
        style={{
          backgroundColor: extractPaletteColor(concept, shadeKey),
        }}
      />
      <TextSmallLight>{shadeName}</TextSmallLight>
    </div>
  );
});
